import { Alert, Button, Empty, Input, Segmented, Space, Spin, Table, Tag, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { tx } from '../../i18n/translate.js';

export interface PrivilegeRow {
  privilegeid: string;
  name: string;
  description?: string;
  usageStatus?: 'active' | 'unused';
  accessright: number | null;
  currentDepth: number;
  canBe: { basic: boolean; local: boolean; deep: boolean; global: boolean };
}

export interface PrivilegeListResponse {
  role: { roleid: string };
  privileges: PrivilegeRow[];
  total: number;
  page: number;
  pageSize: number;
  currentAssignments: Array<{ privilegeid: string; depthmask: number }>;
}

const DEPTH_OPTIONS = [
  { value: 0, label: tx('legacy:pages.admin.roledetaildrawer.ee336179') },
  { value: 1, label: tx('legacy:pages.admin.roledetaildrawer.depthBasic') },
  { value: 2, label: tx('legacy:pages.admin.roledetaildrawer.depthLocal') },
  { value: 4, label: tx('legacy:pages.admin.roledetaildrawer.depthDeep') },
  { value: 8, label: tx('legacy:pages.admin.roledetaildrawer.depthGlobal') },
];

export function RolePrivilegeEditor({
  data,
  loading,
  changes,
  onChange,
  disabled,
  q,
  onQChange,
  page,
  pageSize,
  onPageChange,
  usageStatus = 'active',
  onUsageStatusChange,
  boundaryMessage,
  boundaryDescription,
}: {
  data: PrivilegeListResponse | null;
  loading: boolean;
  changes: Map<string, number>;
  onChange: (m: Map<string, number>) => void;
  disabled: boolean;
  q: string;
  onQChange: (q: string) => void;
  page: number;
  pageSize: number;
  onPageChange: (page: number, pageSize: number) => void;
  usageStatus?: 'active' | 'unused' | 'all';
  onUsageStatusChange?: (status: 'active' | 'unused' | 'all') => void;
  boundaryMessage?: string;
  boundaryDescription?: string;
}) {
  const [searchInput, setSearchInput] = useState(q);

  useEffect(() => {
    setSearchInput(q);
  }, [q]);

  useEffect(() => {
    if (searchInput === q) return;
    const timer = setTimeout(() => onQChange(searchInput), 300);
    return () => clearTimeout(timer);
  }, [searchInput, q, onQChange]);

  const setDepth = (privilegeid: string, currentDepth: number, newDepth: number) => {
    const next = new Map(changes);
    if (newDepth === currentDepth) next.delete(privilegeid);
    else next.set(privilegeid, newDepth);
    onChange(next);
  };

  const batchSetDepth = (newDepth: number) => {
    const next = new Map(changes);
    for (const row of data?.privileges ?? []) {
      if (!isDepthAllowed(row, newDepth)) continue;
      if (newDepth === row.currentDepth) next.delete(row.privilegeid);
      else next.set(row.privilegeid, newDepth);
    }
    onChange(next);
  };

  return (
    <div>
      {boundaryMessage && (
        <Alert
          type="info"
          showIcon
          message={boundaryMessage}
          description={boundaryDescription}
          style={{ marginBottom: 12 }}
        />
      )}
      {disabled && (
        <Alert
          type="info"
          showIcon
          message={tx('legacy:pages.admin.roledetaildrawer.0141adb4')}
          style={{ marginBottom: 12 }}
        />
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          marginBottom: 12,
        }}
      >
        <Input
          placeholder={tx('legacy:pages.admin.roledetaildrawer.6eb40301')}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          style={{ maxWidth: 320 }}
          allowClear
        />
        <Space size={8} wrap style={{ justifyContent: 'flex-end' }}>
          {onUsageStatusChange && (
            <>
              <Segmented
                size="small"
                value={usageStatus}
                onChange={(value) =>
                  onUsageStatusChange(value as 'active' | 'unused' | 'all')
                }
                options={[
                  { label: '已生效', value: 'active' },
                  { label: '未使用', value: 'unused' },
                  { label: '全部', value: 'all' },
                ]}
              />
              <Button
                size="small"
                onClick={() => onUsageStatusChange('active')}
                disabled={usageStatus === 'active'}
              >
                隐藏未使用
              </Button>
            </>
          )}
          {data && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {tx('legacy:dynamic.assignmentSummary', {
                total: data.total,
                granted: data.currentAssignments.length,
              })}
            </Typography.Text>
          )}
        </Space>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Space size={6} wrap>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            当前页一键授予
          </Typography.Text>
          {DEPTH_OPTIONS.filter((option) => option.value > 0).map((option) => (
            <Button
              key={option.value}
              size="small"
              disabled={disabled || !data?.privileges.length}
              onClick={() => batchSetDepth(option.value)}
            >
              {option.label}
            </Button>
          ))}
        </Space>
      </div>
      <Spin spinning={loading} delay={150}>
        <Table
          rowKey="privilegeid"
          dataSource={data?.privileges ?? []}
          size="small"
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: ['15', '25', '50', '100'],
            showTotal: (total, range) =>
              tx('legacy:dynamic.paginationRange', {
                start: range[0],
                end: range[1],
                total,
              }),
            onChange: (nextPage, nextPageSize) => onPageChange(nextPage, nextPageSize),
          }}
          columns={[
            {
              title: tx('legacy:pages.admin.roledetaildrawer.3de149c9'),
              dataIndex: 'name',
              render: (name: string, row) => (
                <div>
                  <Space size={6} wrap>
                    <Typography.Text code style={{ fontSize: 11 }}>
                      {name}
                    </Typography.Text>
                    {row.usageStatus && (
                      <Tag color={row.usageStatus === 'active' ? 'green' : 'default'}>
                        {row.usageStatus === 'active' ? '已生效' : '未使用'}
                      </Tag>
                    )}
                    {changes.has(row.privilegeid) && (
                      <Tag color="orange" style={{ fontSize: 10 }}>
                        {tx('legacy:pages.admin.roledetaildrawer.6f5d885a')}
                      </Tag>
                    )}
                  </Space>
                  {row.description && (
                    <Typography.Text
                      type="secondary"
                      style={{ display: 'block', fontSize: 12, marginTop: 4 }}
                    >
                      {row.description}
                    </Typography.Text>
                  )}
                </div>
              ),
            },
            {
              title: tx('legacy:pages.admin.roledetaildrawer.8842a540'),
              key: 'depth',
              align: 'center',
              width: 380,
              render: (_value, row) => {
                const effective = changes.has(row.privilegeid)
                  ? (changes.get(row.privilegeid) ?? 0)
                  : row.currentDepth;
                return (
                  <Space size={4}>
                    {DEPTH_OPTIONS.map((option) => {
                      const allowed = isDepthAllowed(row, option.value);
                      const active = effective === option.value;
                      return (
                        <Button
                          key={option.value}
                          size="small"
                          type={active ? 'primary' : 'default'}
                          disabled={disabled || !allowed}
                          onClick={() => setDepth(row.privilegeid, row.currentDepth, option.value)}
                          style={{
                            minWidth: 60,
                            fontSize: 11,
                            opacity: !active && !allowed ? 0.4 : 1,
                          }}
                        >
                          {option.label}
                        </Button>
                      );
                    })}
                  </Space>
                );
              },
            },
          ]}
          locale={{
            emptyText: <Empty description={tx('legacy:pages.admin.roledetaildrawer.a591b62a')} />,
          }}
        />
      </Spin>
    </div>
  );
}

function isDepthAllowed(row: PrivilegeRow, depth: number) {
  return (
    depth === 0 ||
    (depth === 1 && row.canBe.basic) ||
    (depth === 2 && row.canBe.local) ||
    (depth === 4 && row.canBe.deep) ||
    (depth === 8 && row.canBe.global)
  );
}
